/**
 * Waitlist Service
 *
 * Handles waitlist signups for pre-CASA launch.
 * Public functions (join, count) use anonymous Supabase access.
 * Admin functions (list all, delete) route through the admin-api edge function
 * which has service role access to both QA and prod databases.
 */

import { supabase } from './supabaseClient';

const ADMIN_API_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-api`;

/**
 * Make an authenticated request to the admin-api edge function
 */
const adminRequest = async (body) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
        throw new Error('Not authenticated');
    }

    const response = await fetch(ADMIN_API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || `Request failed: ${response.status}`);
    }

    return data;
};

/**
 * Detect user's country and city from IP (pre-fetched on load)
 */
let cachedLocation = null;
const locationPromise = (async () => {
    try {
        const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: AbortSignal.timeout(3000) });
        if (!res.ok) return;
        const data = await res.json();
        cachedLocation = {
            country: data.country || null,
            city: data.city || null,
        };
    } catch {
        // silently fail — location is optional
    }
})();

/**
 * Add an email to the waitlist
 * @param {string} email - Email to add
 * @param {string} source - Where they signed up from (default: 'landing_page')
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const joinWaitlist = async (email, source = 'landing_page') => {
    try {
        const normalizedEmail = email.trim().toLowerCase();

        // Use pre-fetched location (non-blocking — if still loading, just use null)
        const country = cachedLocation?.country || null;
        const city = cachedLocation?.city || null;

        const { error } = await supabase
            .from('waitlist')
            .insert({ email: normalizedEmail, source, country, city });

        if (error) {
            // Duplicate email (unique constraint violation)
            if (error.code === '23505') {
                return { success: true, message: "You're already on the list! We'll be in touch soon." };
            }
            throw error;
        }

        return { success: true, message: "You're on the list! We'll email you when we launch." };
    } catch (error) {
        console.error('Waitlist signup error:', error);
        return { success: false, message: 'Something went wrong. Please try again.' };
    }
};

/**
 * Get the current waitlist count
 * @returns {Promise<number>}
 */
export const getWaitlistCount = async () => {
    try {
        const { count, error } = await supabase
            .from('waitlist')
            .select('*', { count: 'exact', head: true });

        if (error) throw error;
        return count || 0;
    } catch (error) {
        console.error('Waitlist count error:', error);
        return 0;
    }
};

/**
 * Get all waitlist entries (admin use) — single env
 * @returns {Promise<Array>}
 */
export const getWaitlistEntries = async () => {
    try {
        const { data, error } = await supabase
            .from('waitlist')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Waitlist list error:', error);
        return [];
    }
};

/**
 * Get waitlist entries from BOTH QA and Prod via admin-api edge function.
 * Uses service role access to fetch from both databases.
 * @returns {Promise<Array>}
 */
export const getAllWaitlistEntries = async () => {
    try {
        return await adminRequest({ action: 'list-waitlist' });
    } catch (error) {
        console.error('Waitlist list error:', error);
        return [];
    }
};

/**
 * Delete a waitlist entry by id via admin-api edge function.
 * @param {string} id
 * @param {string} env - 'QA' or 'PROD'
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteWaitlistEntry = async (id, env = 'QA') => {
    try {
        const result = await adminRequest({
            action: 'delete-waitlist',
            id,
            env: env.toLowerCase(),
        });
        return { success: true, message: result.message || 'Entry deleted.' };
    } catch (error) {
        console.error('Waitlist delete error:', error);
        return { success: false, message: error.message };
    }
};

export default { joinWaitlist, getWaitlistCount, getWaitlistEntries, getAllWaitlistEntries, deleteWaitlistEntry };
